import { SATError, SATResult } from "@sadtx/sat-error";
import {
  StoreGuardian,
  StoreOrder,
  StorePatientContext,
  StorePaymentHistoryPage,
  StoreProduct,
  StorePurchaseResult,
  StoreRefundQuote,
  StoreRefundRequest,
  StoreRefundResult,
  StoreSettlementPage,
  TreatmentVoucherClaim,
  StoreBrandpayCard,
  StoreBrandpayCustomer,
  StorePaymentFlow,
} from "../entity/store-entities";
import { StoreRepository } from "../repository/store-repository";

/** 판매 상품 목록 조회 */
export class FetchStoreProductsUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(): Promise<SATResult<StoreProduct[], SATError>> {
    return this.repository.fetchProducts();
  }
}

/** 브랜드페이 고객 키 — 의료진 본인 단위. SDK 초기화(customerKey)에 쓴다 */
export class FetchBrandpayCustomerUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(): Promise<SATResult<StoreBrandpayCustomer, SATError>> {
    return this.repository.fetchBrandpayCustomer();
  }
}

/** 본인이 브랜드페이에 등록한 카드 목록 */
export class FetchBrandpayCardsUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(): Promise<SATResult<StoreBrandpayCard[], SATError>> {
    return this.repository.fetchBrandpayCards();
  }
}

/**
 * 환자 컨텍스트 확인 — 환자 상세 '치료 추가'로 진입 시 전달된 patientUuid 로
 * 환자·보호자를 조회한다 (기존 치료 목록 API 재사용, 전화번호 마스킹).
 */
export class ResolveStorePatientContextUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(patientUuid: string): Promise<SATResult<StorePatientContext, SATError>> {
    return this.repository.resolvePatientContext(patientUuid);
  }
}

/**
 * 보호자 이름 + 전화번호로 보호자(및 연결된 자녀) 조회 (1안 방식 폴백) — 의료 서버 API
 * 전화번호는 숫자만 남기고 정규화하여 요청한다.
 */
export class SearchStoreGuardianUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(
    guardianName: string,
    guardianPhone: string
  ): Promise<SATResult<StoreGuardian | null, SATError>> {
    return this.repository.searchGuardian(guardianName.trim(), guardianPhone.replace(/\D/g, ""));
  }
}

/**
 * 위젯(일반) 결제용 주문 생성 — 금액은 서버가 산출.
 * 환자 이름은 리포지토리에서 마스킹되어 스냅샷으로만 전달된다.
 */
export class PrepareStoreOrderUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(
    productId: string,
    patientUuid: string,
    patientName: string,
    paymentFlow: StorePaymentFlow
  ): Promise<SATResult<StoreOrder, SATError>> {
    return this.repository.prepareOrder(productId, patientUuid, patientName, paymentFlow);
  }
}

/**
 * 위젯(일반) 결제 승인 + 이용권 발급 (서버 원자 처리, 금액 위변조 검증)
 * 치료 등록(시작일 선택)은 치료 등록 화면에서 진행한다.
 */
export class ConfirmStorePurchaseUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(
    paymentKey: string,
    orderId: string,
    amount: number,
    pgProvider: string
  ): Promise<SATResult<StorePurchaseResult, SATError>> {
    return this.repository.confirmPurchase(paymentKey, orderId, amount, pgProvider);
  }
}

/**
 * 결제 영수증으로 치료 이용권 자격 발급 — 의료 서버 claim (멱등).
 * 결제 완료 직후 호출하고, 실패하면 같은 영수증으로 재시도한다.
 */
export class ClaimTreatmentVoucherUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(orderId: string, receipt: string): Promise<SATResult<TreatmentVoucherClaim, SATError>> {
    return this.repository.claimTreatmentVoucher(orderId, receipt);
  }
}

/**
 * 미수령 주문 재제출 — 결제는 됐는데 자격 발급(claim)을 못 마친 주문.
 * store 에서 영수증을 다시 받아 의료 서버 claim (멱등) 후 수령 표시.
 */
export class ReclaimStoreOrderUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(orderId: string): Promise<SATResult<TreatmentVoucherClaim, SATError>> {
    return this.repository.reclaimOrder(orderId);
  }
}

/**
 * 환불 1단계 — 의료 서버 증서(이용권 잠금·공급 정보) → store 견적.
 * 반환된 견적(규칙·금액)을 사용자에게 보여주고, 확정 시 ExecuteRefundUsecase, 취소 시 CancelRefundUsecase.
 */
export class PrepareRefundUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(orderId: string): Promise<SATResult<StoreRefundQuote, SATError>> {
    return this.repository.prepareRefund(orderId);
  }
}

/** 환불 2단계 — store PG 취소(전액/일할) → 의료 서버 자격 회수(활성 치료 종료). 서버 간 통신 없음 */
export class ExecuteRefundUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(orderId: string, attestation: string): Promise<SATResult<StoreRefundResult, SATError>> {
    return this.repository.executeRefund(orderId, attestation);
  }
}

/** 환불 중단 — 견적만 보고 취소한 경우 의료 서버 잠금 해제 */
export class CancelRefundUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(orderId: string): Promise<SATResult<void, SATError>> {
    return this.repository.cancelRefund(orderId);
  }
}

/** 예외 환불 요청 — 기기 결함·처방의 중단 지시·중대 질환·기술적 사유 (SA 관리자 승인 후 전액) */
export class RequestExceptionRefundUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(orderId: string, reason: string, detail: string): Promise<SATResult<StoreRefundRequest, SATError>> {
    return this.repository.requestExceptionRefund(orderId, reason, detail.trim());
  }
}

/** 관리자(에스알파): 예외 환불 요청 목록 */
export class FetchRefundRequestsUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(status: string | null): Promise<SATResult<StoreRefundRequest[], SATError>> {
    return this.repository.fetchRefundRequests(status);
  }
}

/** 관리자(에스알파): 예외 환불 요청 승인/거절 */
export class DecideRefundRequestUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(
    requestId: number,
    decision: "APPROVE" | "REJECT",
    note: string | null
  ): Promise<SATResult<StoreRefundRequest, SATError>> {
    return this.repository.decideRefundRequest(requestId, decision, note);
  }
}

/** 관리자(에스알파): 활성화 기록·수수료 정산 조회 */
export class FetchStoreSettlementsUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(month: string): Promise<SATResult<StoreSettlementPage, SATError>> {
    return this.repository.fetchSettlements(month);
  }
}

/**
 * 결제 내역 조회 (서버 페이지네이션)
 * 병원 범위는 스토어 토큰의 hospitalCode 로 서버가 강제하고, 환자명은 마스킹 스냅샷으로 반환된다.
 */
export class FetchStorePaymentHistoryUsecase {
  constructor(private readonly repository: StoreRepository) {}

  async execute(
    page: number,
    size: number,
    status: string | null,
    query: string | null
  ): Promise<SATResult<StorePaymentHistoryPage, SATError>> {
    return this.repository.fetchPaymentHistory(page, size, status, query);
  }
}
